import {
  Box,
  Flex,
  Heading,
  Skeleton,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
} from '@chakra-ui/react';
import * as _ from 'lodash';
import React from 'react';
import { Navigate } from 'react-router-dom';

import { date as commonDate, finleyAssert } from '@finley-technologies/common';

import {
  BankAccountWithInstitutionAndDesignationGQLType,
  useGetUserQuery,
} from '../../../../../../types/dashboard-core-gqlTypes.generated.d';
import { colors } from 'packages/common-frontend/colors';
import { BackButton } from 'packages/common-frontend/components/BackButton';
import { useCreditFacilityUiVisibility } from 'packages/common-frontend/hooks/useUiVisibility';
import {
  getUnexpectedErrorMessage,
  bankAccountToDisplayString,
  dollarNumberWithCommas,
} from 'packages/common-frontend/utils';
import { CreditFacilitySelect } from 'packages/credit-facility/ui/CreditFacilitySelect';
import { useGetCurrentCreditAgreementFullArtifactQuery } from 'packages/creditAgreements/requests/requests.gql.generated';
import { parseCreditAgreementFullArtifact } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { useGetFundingRequestWithBaseDetailsAndBorrowingBaseQuery } from 'packages/funding-requests/requests/requests.gql.generated';
import { FundingRequestDetailBorrowingBaseSummary } from 'packages/funding-requests/ui/FundingRequestDetailBorrowingBaseSummary';
import { FundingRequestReports } from 'packages/funding-requests/ui/FundingRequestReports';
import {
  FundingRequestEventHistory,
  VerificationReportEventHistory,
} from 'packages/funding-requests/ui/shared/FundingRequestEvents';
import { FundingRequestStatusBadge } from 'packages/funding-requests/ui/shared/StatusBadge';
import { VerificationApproveTables } from 'packages/verifications/ui/VerificationApproveTables';
import { useFeatureFlags } from '~/contexts/FeatureFlagProvider';
import { SingleEntityDrawer as FundingRequestEventLogSingleEntityDrawer } from '~/core/routes/funding-requests/EntityEventLogDrawers';
import { errorToastConfig } from '~/packages/common-frontend/constants';
import { fundingRequestKeyToLabel } from '~/packages/funding-requests/constants';

// Exports
type Props = {
  fundingRequestId: string;
  creditFacilityId: string;
};
export const FundingRequestDetailViewContainer = ({
  fundingRequestId,
  creditFacilityId,
}: Props) => {
  const toast = useToast();
  const flags = useFeatureFlags();

  // @JonAtFinley @danitian replace with real UiVisibility returned from auth
  const facilityUiVisibility = useCreditFacilityUiVisibility({
    mockUiVisibilityMode: flags.isBorrowerLimitedOrganization
      ? 'borrowerLimited'
      : 'borrowerOwner',
    creditFacilityId,
  });

  const { data: userData, loading: userLoading } = useGetUserQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
  });
  const {
    data: fundingRequestData,
    loading: loadingFundingRequestData,
    error,
  } = useGetFundingRequestWithBaseDetailsAndBorrowingBaseQuery({
    variables: { fundingRequestId },
    fetchPolicy: 'network-only',
  });

  const {
    data: creditAgreementData,
    loading: isCreditAgreementLoading,
  } = useGetCurrentCreditAgreementFullArtifactQuery({
    variables: { creditFacilityId },
    onError: () => {
      toast({
        ...errorToastConfig,
        id: 'errorGetCurrentCreditAgreementFullArtifact',
        title: 'Error loading credit agreement.',
      });
    },
  });

  if (
    !fundingRequestData ||
    loadingFundingRequestData ||
    !userData ||
    userLoading ||
    isCreditAgreementLoading ||
    !creditAgreementData ||
    !creditAgreementData.currentCreditAgreementFullArtifact
  ) {
    return <Skeleton data-testid="loading-funding-request" height="600px" />;
  }
  if (error) {
    return <Navigate to="/funding-requests" />;
  }

  const {
    fundingRequestWithFullDetails: {
      bankAccounts,
      created_at: createdAt,
      creditFacility,
      fundingRequestComponents,
      fundingRequestEvents,
      fundingRequestReports,
      status,
      verificationReport,
      verificationReportEvents,
      borrowingBaseReportId,
      reportingDate,
      borrowingBaseReport: { fileVersion },
    },
  } = fundingRequestData;
  finleyAssert(
    fundingRequestEvents,
    'Funding request events expected to exist for funding request with full details',
  );

  const bankAccountsMap = new Map<
    string,
    BankAccountWithInstitutionAndDesignationGQLType
  >(
    bankAccounts.map(
      (
        bankAccount: BankAccountWithInstitutionAndDesignationGQLType,
      ): [string, BankAccountWithInstitutionAndDesignationGQLType] => [
        bankAccount.id,
        bankAccount,
      ],
    ),
  );

  const {
    currentCreditAgreementFullArtifact: {
      creditAgreement,
      id: creditAgreementId,
    },
  } = creditAgreementData;

  const parsedCreditAgreement = parseCreditAgreementFullArtifact(
    creditAgreement,
  );

  finleyAssert(
    parsedCreditAgreement,
    `Expect parsed currentCreditAgreementFullArtifact to exist for [creditFacilityId ${creditFacilityId}] [creditAgreementId: ${creditAgreementId}]`,
  );

  return (
    <>
      <Flex direction="column" gap="8">
        <Flex justifyContent="space-between" wrap="wrap">
          <Flex direction="column">
            <Flex gap=".5rem" direction={{ base: 'column', md: 'row' }}>
              <Heading as="h1" fontSize="4xl">
                Funding Request
              </Heading>
              <FundingRequestStatusBadge status={status} />
            </Flex>
            <Text color="gray">
              {commonDate.dateToHumanDateString(new Date(createdAt))}
            </Text>
          </Flex>
          <Flex pt={{ base: '1', md: '0' }} gap="0.5rem">
            <BackButton />
            <FundingRequestEventLogSingleEntityDrawer
              entityId={fundingRequestId}
            />
          </Flex>
        </Flex>
        <CreditFacilitySelect isDisabled />
      </Flex>
      <Box mt="35px">
        <Heading as="h3" fontSize="2xl" paddingTop="10px">
          Transaction Details
        </Heading>
        <Table variant="simple" mt="35px">
          <Thead
            background={colors.tableHeader}
            borderBottom="1px solid lightgrey"
            padding="8px 0"
          >
            <Tr>
              <Th textAlign="left" width="20%">
                Transaction
              </Th>
              <Th textAlign="center" width="10%">
                Amount
              </Th>
              <Th textAlign="center" width="20%">
                Source
              </Th>
              <Th textAlign="center" width="20%">
                Destination
              </Th>
              <Th textAlign="center" width="15%">
                Funding Date
              </Th>
              <Th textAlign="center" width="15%">
                Settled Date
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {_.map(
              fundingRequestComponents,
              ({
                id,
                type,
                amount,
                sourceBankAccountId,
                destinationBankAccountId,
                targetFundingDate,
                postingDate,
              }) => {
                const destinationBankAccount = bankAccountsMap.get(
                  destinationBankAccountId,
                );
                const sourceBankAccount = bankAccountsMap.get(
                  sourceBankAccountId,
                );
                return (
                  !!amount && (
                    <Tr key={`row-${id}`}>
                      <Td textAlign="left" width="20%">
                        {fundingRequestKeyToLabel[type]}
                      </Td>
                      <Td textAlign="center" width="10%">
                        {dollarNumberWithCommas(amount)}
                      </Td>
                      <Td textAlign="center" width="20%">
                        {bankAccountToDisplayString(sourceBankAccount)}
                      </Td>
                      <Td textAlign="center" width="20%">
                        {bankAccountToDisplayString(destinationBankAccount)}
                      </Td>
                      <Td textAlign="center" width="15%">
                        {targetFundingDate
                          ? commonDate.fullDateStrToHumanDateString(
                              targetFundingDate,
                            )
                          : '-'}
                      </Td>
                      <Td textAlign="center" width="15%">
                        {postingDate
                          ? commonDate.fullDateStrToHumanDateString(postingDate)
                          : '-'}
                      </Td>
                    </Tr>
                  )
                );
              },
            )}
          </Tbody>
        </Table>
        {fundingRequestReports && (
          <FundingRequestReports reports={fundingRequestReports} />
        )}
        {facilityUiVisibility.fundingRequest
          .showVerificationReportIfAvailable && verificationReport ? (
          (status === 'rejected' || status === 'approved') && (
            <Box pt="32px">
              <Heading as="h3" fontSize="2xl" pb="16px">
                Borrowing Base Summary
              </Heading>
              <Box
                borderWidth="2px"
                borderRadius="8px"
                padding="0 24px 24px 24px"
                borderColor={colors.gray[200]}
              >
                <VerificationApproveTables
                  creditFacilityId={creditFacilityId}
                  creditAgreement={parsedCreditAgreement}
                  verificationReport={JSON.parse(
                    verificationReport.verificationReport,
                  )}
                  verificationReportId={verificationReport.verificationReportId}
                  verificationReportBorrowingBaseReportId={
                    verificationReport.borrowingBaseReportId
                  }
                  approvalReportNote={
                    verificationReport.approvalReportNote ?? undefined
                  }
                  selectedReportingDate={reportingDate}
                  fileVersion={fileVersion}
                  artifactDocumentId={verificationReport.artifactDocumentId}
                  viewMode="viewOnly"
                />
              </Box>
            </Box>
          )
        ) : (
          <FundingRequestDetailBorrowingBaseSummary
            borrowingBaseReportId={borrowingBaseReportId}
            creditFacilityId={creditFacilityId}
          />
        )}
        <Heading as="h3" fontSize="2xl" paddingTop="10px" mt="35px" mb="25px">
          Event History
        </Heading>
        {fundingRequestEvents && creditFacility.name && (
          <FundingRequestEventHistory
            events={fundingRequestEvents}
            bankAccountsMap={bankAccountsMap}
            creditFacilityName={creditFacility.name}
          />
        )}
        {verificationReportEvents && creditFacility.name && (
          <VerificationReportEventHistory
            events={verificationReportEvents}
            creditFacilityName={creditFacility.name}
          />
        )}
      </Box>
    </>
  );
};
